import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Dialog, FormRow, RadioCard, TextField, Textarea } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { addDays, isValidDayKey, isValidTime, todayKey } from '../tasks/dates';
import {
  useRecordOutcome,
  type AppointmentDetail,
  type AppointmentOutcome,
  type OutcomeNextStep,
  type OutcomeResponse,
} from './api';
import styles from './OutcomeDialog.module.css';

type DoneResponse = Extract<OutcomeResponse, { next: 'done' }>;

export interface OutcomeFormProps {
  appointmentId: string;
  contactId: string | null;
  contactName: string | null;
  eventTypeName: string;
  /** „Erstgespräch Kapitalanlage · heute 10:00–10:45 · Video · J. Weiler" */
  subtitle: string;
  /** `queue`: „Speichern und weiter" with the footer note; `detail`: „Speichern". */
  variant: 'queue' | 'detail';
  /** Called once the recorded outcome (and its hints) were acknowledged. */
  onDone: (result: DoneResponse) => void | Promise<void>;
  /** Outcome „Verschoben": nothing is stored, the reschedule flow opens. */
  onReschedule: (appointmentId: string) => void;
  /** Renders the card chrome (Queue 05/06) around the form. */
  card?: boolean | undefined;
}

type AttendedStep = 'send_self_disclosure' | 'book_appointment' | 'task';
type NoShowStep = 'send_booking_link' | 'call_tomorrow' | 'lifecycle_end';

const RESULT_OPTIONS: ReadonlyArray<{ value: AppointmentOutcome; title: string; description: string }> = [
  { value: 'attended', title: 'Wahrgenommen', description: 'Termin fand statt' },
  { value: 'no_show', title: 'No-Show', description: 'Kunde nicht erschienen' },
  { value: 'rescheduled', title: 'Verschoben', description: 'auf neuen Termin' },
];

const TEXT_REQUIRED = 'Bitte geben Sie das Ergebnis an.';
const TITLE_REQUIRED = 'Bitte einen Titel angeben.';
const DATE_REQUIRED = 'Bitte ein Datum wählen.';
const TIME_INVALID = 'Bitte eine Uhrzeit im Format HH:MM angeben.';
const REASON_REQUIRED = 'Bitte geben Sie einen Grund an.';

/**
 * Queue 05/06 „Wie lief der Termin?": result tiles, the mandatory result text (attended), the follow-up
 * options per result mapped to the server's next-step kinds. The server's stage suggestion and the
 * no-show escalation hints arrive with the response and are shown before the form hands over.
 */
export function OutcomeForm({
  appointmentId,
  contactId,
  contactName,
  eventTypeName,
  subtitle,
  variant,
  onDone,
  onReschedule,
  card = false,
}: OutcomeFormProps) {
  const record = useRecordOutcome();
  const today = todayKey();
  const [outcome, setOutcome] = useState<AppointmentOutcome>('attended');
  const [text, setText] = useState('');
  const [attendedStep, setAttendedStep] = useState<AttendedStep>('send_self_disclosure');
  const [noShowStep, setNoShowStep] = useState<NoShowStep>('send_booking_link');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDueOn, setTaskDueOn] = useState(() => addDays(todayKey(), 1));
  const [taskDueTime, setTaskDueTime] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<DoneResponse | null>(null);

  const name = contactName ?? 'Der Kontakt';
  const serverFields = record.error instanceof ApiError ? record.error.fields : {};
  const fieldError = (key: string) => errors[key] ?? serverFields[key];

  function buildNextStep(): OutcomeNextStep | null {
    if (outcome === 'attended') {
      if (attendedStep === 'task') {
        return {
          kind: 'task',
          title: taskTitle.trim(),
          dueOn: taskDueOn,
          ...(taskDueTime !== '' ? { dueTime: taskDueTime } : {}),
        };
      }
      return { kind: attendedStep };
    }
    if (noShowStep === 'lifecycle_end')
      return { kind: 'lifecycle_end', target: 'no_interest', reason: reason.trim() };
    return { kind: noShowStep };
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (record.isPending) return;
    if (outcome === 'rescheduled') {
      record.mutate(
        { id: appointmentId, body: { outcome: 'rescheduled' } },
        {
          onSuccess: (response) => {
            if (response.next === 'reschedule') onReschedule(response.appointmentId);
          },
        },
      );
      return;
    }
    const next: Record<string, string> = {};
    if (outcome === 'attended' && text.trim() === '') next['text'] = TEXT_REQUIRED;
    if (outcome === 'attended' && attendedStep === 'task') {
      if (taskTitle.trim() === '') next['taskTitle'] = TITLE_REQUIRED;
      if (!isValidDayKey(taskDueOn)) next['taskDueOn'] = DATE_REQUIRED;
      if (taskDueTime !== '' && !isValidTime(taskDueTime)) next['taskDueTime'] = TIME_INVALID;
    }
    if (outcome === 'no_show' && noShowStep === 'lifecycle_end' && reason.trim() === '')
      next['reason'] = REASON_REQUIRED;
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const step = buildNextStep();
    record.mutate(
      {
        id: appointmentId,
        body: {
          outcome,
          ...(outcome === 'attended' ? { text: text.trim() } : {}),
          ...(step ? { nextStep: step } : {}),
        },
      },
      {
        onSuccess: (response) => {
          if (response.next !== 'done') return;
          const hasHints =
            response.stageSuggestion !== null || response.secondNoShowInARow || response.suggestNoInterest;
          if (hasHints) setDone(response);
          else void onDone(response);
        },
      },
    );
  }

  const submitLabel =
    outcome === 'rescheduled'
      ? 'Weiter zum Verschieben'
      : variant === 'queue'
        ? 'Speichern und weiter'
        : 'Speichern';

  let body: ReactNode;
  if (done !== null) {
    body = (
      <div className={styles.stack}>
        <Banner tone="success">
          {outcome === 'attended' ? `Termin wahrgenommen: ${eventTypeName}` : `No-Show: ${eventTypeName}`}
          {done.createdTaskId ? ' · Folgeschritt angelegt' : ''}
        </Banner>
        {done.stageSuggestion ? (
          <div className={clsx(styles.box, styles.boxInfo)}>
            <span className={clsx(styles.boxTitle, styles.boxTitleInfo)}>Nächster Stage</span>
            {done.stageSuggestion}
          </div>
        ) : null}
        {done.suggestNoInterest ? (
          <div className={clsx(styles.box, styles.boxWarning)}>
            <span className={clsx(styles.boxTitle, styles.boxTitleWarning)}>Dritter No-Show in Folge</span>
            DAMOKLES schlägt „Kein Interesse“ vor.
            {contactId ? (
              <>
                {' '}
                <Link to={`/contacts/${contactId}`}>Lifecycle in der Kontaktakte ändern</Link>
              </>
            ) : null}
          </div>
        ) : done.secondNoShowInARow ? (
          <div className={styles.hint}>
            Zweiter No-Show in Folge. Beim dritten schlägt DAMOKLES „Kein Interesse“ vor.
          </div>
        ) : null}
        <div className={styles.footer}>
          <span className={styles.footerNote}>
            {variant === 'queue' ? 'Weiter schaltet zur nächsten Task' : ''}
          </span>
          <span className={styles.footerActions}>
            <Button onClick={() => void onDone(done)}>Weiter</Button>
          </span>
        </div>
      </div>
    );
  } else {
    body = (
      <form className={styles.stack} onSubmit={submit} noValidate>
        {record.isError && Object.keys(serverFields).length === 0 ? (
          <Banner tone="danger">{errorMessage(record.error)}</Banner>
        ) : null}
        <div className={styles.tiles3} role="radiogroup" aria-label="Ergebnis">
          {RESULT_OPTIONS.map((option) => (
            <RadioCard
              key={option.value}
              name={`outcome-${appointmentId}`}
              value={option.value}
              title={option.title}
              description={option.description}
              checked={outcome === option.value}
              onChange={() => {
                setOutcome(option.value);
                setErrors({});
              }}
            />
          ))}
        </div>

        {outcome === 'attended' ? (
          <>
            <TextField
              label="Ergebnis"
              required
              value={text}
              error={fieldError('text')}
              onChange={(event) => setText(event.target.value)}
            />
            <div
              className={clsx(styles.tiles3, styles.tilesTight)}
              role="radiogroup"
              aria-label="Folgeschritt"
            >
              <RadioCard
                name={`attended-step-${appointmentId}`}
                value="send_self_disclosure"
                title="Selbstauskunft senden"
                description="Link per E-Mail"
                checked={attendedStep === 'send_self_disclosure'}
                onChange={() => setAttendedStep('send_self_disclosure')}
              />
              <RadioCard
                name={`attended-step-${appointmentId}`}
                value="book_appointment"
                title="Folgetermin buchen"
                checked={attendedStep === 'book_appointment'}
                onChange={() => setAttendedStep('book_appointment')}
              />
              <RadioCard
                name={`attended-step-${appointmentId}`}
                value="task"
                title="Aufgabe anlegen"
                checked={attendedStep === 'task'}
                onChange={() => setAttendedStep('task')}
              />
            </div>
            {attendedStep === 'task' ? (
              <FormRow tracks="minmax(0,2fr) minmax(0,1fr) minmax(0,1fr)">
                <TextField
                  label="Titel"
                  required
                  value={taskTitle}
                  error={fieldError('taskTitle')}
                  onChange={(event) => setTaskTitle(event.target.value)}
                />
                <TextField
                  label="Fällig am"
                  type="date"
                  required
                  min={today}
                  value={taskDueOn}
                  error={fieldError('taskDueOn')}
                  onChange={(event) => setTaskDueOn(event.target.value)}
                />
                <TextField
                  label="Uhrzeit"
                  type="time"
                  value={taskDueTime}
                  error={fieldError('taskDueTime')}
                  onChange={(event) => setTaskDueTime(event.target.value)}
                />
              </FormRow>
            ) : null}
          </>
        ) : null}

        {outcome === 'no_show' ? (
          <>
            <div className={clsx(styles.box, styles.boxWarning)}>
              <span className={clsx(styles.boxTitle, styles.boxTitleWarning)}>No-Show</span>
              {name} ist nicht erschienen. Lifecycle wechselt auf „No Show“. Senden Sie direkt einen neuen
              Buchungslink oder planen Sie einen Anruf.
            </div>
            <div
              className={clsx(styles.tiles3, styles.tilesTight)}
              role="radiogroup"
              aria-label="Folgeschritt"
            >
              <RadioCard
                name={`noshow-step-${appointmentId}`}
                value="send_booking_link"
                title="Neuen Buchungslink senden"
                description={`Ereignistyp ${eventTypeName}`}
                checked={noShowStep === 'send_booking_link'}
                onChange={() => setNoShowStep('send_booking_link')}
              />
              <RadioCard
                name={`noshow-step-${appointmentId}`}
                value="call_tomorrow"
                title="Anruf morgen, 9:00"
                checked={noShowStep === 'call_tomorrow'}
                onChange={() => setNoShowStep('call_tomorrow')}
              />
              <RadioCard
                name={`noshow-step-${appointmentId}`}
                value="lifecycle_end"
                title="Kein Interesse"
                description="mit Grund"
                checked={noShowStep === 'lifecycle_end'}
                onChange={() => setNoShowStep('lifecycle_end')}
              />
            </div>
            {noShowStep === 'lifecycle_end' ? (
              <Textarea
                label="Grund"
                required
                rows={2}
                value={reason}
                error={fieldError('reason')}
                onChange={(event) => setReason(event.target.value)}
              />
            ) : null}
          </>
        ) : null}

        {outcome === 'rescheduled' ? (
          <div className={clsx(styles.box, styles.boxNeutral)}>
            Der Termin wird nicht als Ergebnis gespeichert — im nächsten Schritt wählen Sie den neuen
            Zeitpunkt.
          </div>
        ) : null}

        <div className={styles.footer}>
          <span className={styles.footerNote}>
            {variant === 'queue' ? 'Speichern schaltet zur nächsten Task' : ''}
          </span>
          <span className={styles.footerActions}>
            <Button type="submit" loading={record.isPending}>
              {submitLabel}
            </Button>
          </span>
        </div>
      </form>
    );
  }

  return (
    <div className={clsx(card && styles.card)}>
      <div className={styles.cardTitle}>Wie lief der Termin?</div>
      <div className={styles.cardSubtitle}>{subtitle}</div>
      <div className={styles.body}>{body}</div>
    </div>
  );
}

export interface OutcomeDialogProps {
  open: boolean;
  appointment: AppointmentDetail;
  onClose: () => void;
  onDone: (result: DoneResponse) => void | Promise<void>;
  onReschedule: (appointmentId: string) => void;
}

/** The outcome form as a dialog (Queue 09 „Ergebnis erfassen" on the appointment detail). */
export function OutcomeDialog({ open, appointment, onClose, onDone, onReschedule }: OutcomeDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={640}
      title="Termin-Ergebnis"
      eyebrow={appointment.eventType.name}
    >
      <OutcomeForm
        appointmentId={appointment.id}
        contactId={appointment.contact?.id ?? null}
        contactName={appointment.contact?.name ?? appointment.guestName}
        eventTypeName={appointment.eventType.name}
        subtitle={appointment.timeRangeText}
        variant="detail"
        onDone={onDone}
        onReschedule={onReschedule}
      />
    </Dialog>
  );
}
